import type {User} from "../../../data/models/User";
import type {UIVersion} from "../../../data/state/AppState";
import {
	GetOrganizationWideNavigationMenus,
	GetPortfolioWideNavigationMenus,
	NavigationEnum,
	PermissionSetSubNav,
	SettingsModuleNav,
	SettingsModuleSubNav,
	SettingsPermissionsSubNav,
	SettingsSubNav,
} from "../../../Enums";

interface IHashPattern {
	menu: NavigationEnum;
	pattern: RegExp;
}

export class StringUtils {
	// TODO it could be better to convert the ids when loading the models, then it only needs to be done once?
	// "f12" -> 12
	public static refId2Number(refId: string): string | number {
		if (refId?.substring) {
			return Number(refId.substring(1));
		}
		return refId;
	}

	// Converts "DashedLine" to "Dashed Line"
	public static pascalToSpacedName(valueInPascalCase: string): string {
		return (
			valueInPascalCase
				// Add a space before each uppercase letter except the first one
				.replace(/([A-Z])/g, " $1")
				// Remove any leading space that might occur
				.trim()
		);
	}

	public static readonly xyiconOrBoundaryRefIdPattern = new RegExp(`^(x\\d+|b\\d+)$`);

	public static readonly uuidPattern = new RegExp(`[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}`);

	private static readonly spaceBasePattern = new RegExp(`^#?app/${this.uuidPattern.source}/p[0-9]+/space/`);

	private static readonly spacePattern = new RegExp(`${this.spaceBasePattern.source}(s\\d+)$`);

	public static getHashBasePatterns(currentUIVersion: UIVersion): IHashPattern[] {
		const uuid = this.uuidPattern.source;

		return [
			...GetOrganizationWideNavigationMenus(currentUIVersion).map((menu: NavigationEnum) => ({
				menu,
				pattern: new RegExp(`^app/${uuid}/${menu}($|/)`),
			})),
			...GetPortfolioWideNavigationMenus().map((menu: NavigationEnum) => ({
				menu,
				pattern: new RegExp(`^app/${uuid}/p[0-9]+/${menu}($|/)`),
			})),
		];
	}

	public static isHashValidForSpaceBase(hash: string): boolean {
		return this.spaceBasePattern.test(hash);
	}

	public static isHashValidForSpace(hash: string): boolean {
		return this.spacePattern.test(hash);
	}

	public static getSpaceRefIdFromHash(hash: string): string {
		const match = hash.match(this.spacePattern);

		if (match) {
			return match[1];
		}

		return "";
	}

	public static cleanHash(hash: string): string {
		// Remove leading "#"
		return hash.startsWith("#") ? hash.slice(1) : hash;
	}

	public static isHashValid(hash: string, currentUIVersion: UIVersion, user: User): boolean {
		const isAdmin = user?.isAdmin;

		// General structure must start with: app/<orgId>/...
		// UUID v4 regex
		const uuid = this.uuidPattern.source;

		//
		// 1) Organization-level modules
		// app/33261f51-672a-423f-95b2-fceba9bf9175/catalog
		//
		const orgModules = [NavigationEnum.PORTFOLIOS, NavigationEnum.CATALOG, NavigationEnum.REPORTS];
		if (currentUIVersion === "5.0") {
			orgModules.push(NavigationEnum.HOME);
		}
		const orgRoute = new RegExp(`^app/${uuid}/(${orgModules.join("|")})$`);

		//
		// 2) Settings routes
		// settings/personal
		// settings/catalog/types
		//
		const nonAdminSettingsRoute = new RegExp(`^app/${uuid}/${NavigationEnum.SETTINGS}/(${SettingsSubNav.PERSONAL}|${SettingsSubNav.ORGANIZATION})$`);
		const settingsRoutes = [nonAdminSettingsRoute];

		if (isAdmin) {
			const moduleBasePattern = `^app/${uuid}/${NavigationEnum.SETTINGS}/${SettingsSubNav.MODULES}/(${Object.values(SettingsModuleNav).join("|")})`;

			settingsRoutes.push(
				new RegExp(`^app/${uuid}/${NavigationEnum.SETTINGS}/(${SettingsSubNav.PERSONAL}|${SettingsSubNav.ORGANIZATION})$`),
				new RegExp(
					`^app/${uuid}/${NavigationEnum.SETTINGS}/${SettingsSubNav.PERMISSIONS}/(${SettingsPermissionsSubNav.USERS}|${SettingsPermissionsSubNav.USER_GROUPS}|${SettingsPermissionsSubNav.PORTFOLIO_GROUPS})$`,
				),
				new RegExp(
					`^app/${uuid}/${NavigationEnum.SETTINGS}/${SettingsSubNav.PERMISSIONS}/${SettingsPermissionsSubNav.PERMISSION_SETS}/(${Object.values(PermissionSetSubNav).join("|")})$`,
				),
				new RegExp(`${moduleBasePattern}/(${SettingsModuleSubNav.TYPES}|${SettingsModuleSubNav.FIELDS}|${SettingsModuleSubNav.LAYOUTS})$`),
			);

			if (currentUIVersion === "4.0") {
				const adminSettingsRoute4 = new RegExp(`${moduleBasePattern}/${SettingsModuleSubNav.NAVIGATION}$`);
				settingsRoutes.push(adminSettingsRoute4);
			}
		}

		//
		// 3) Portfolio-level modules
		// p5/xyicons
		//
		const portfolioModules = [NavigationEnum.XYICONS, NavigationEnum.BOUNDARIES, NavigationEnum.SPACES, NavigationEnum.REPORT_VIEWS].join("|");
		const portfolioRoute = new RegExp(`^app/${uuid}/p[0-9]+/(${portfolioModules})$`);

		//
		// 4) Space-level routes
		// p5/spaces
		//
		const spacesRoute = new RegExp(`^app/${uuid}/p[0-9]+/spaces$`);

		//
		// 5) Portfolio-level details routes
		// p5/details/xyicon/x32
		// p5/details/boundary/b52
		//
		const detailsRoute = new RegExp(`^app/${uuid}/p[0-9]+/${NavigationEnum.DETAILS}/(xyicon/x[0-9]+|boundary/b[0-9]+)$`);

		// Combine all tests
		const tests = [orgRoute, ...settingsRoutes, portfolioRoute, spacesRoute, detailsRoute, this.spacePattern];

		if (currentUIVersion === "4.0") {
			const orgRouteWithActivePortfolio = new RegExp(`^app/${uuid}/${NavigationEnum.PORTFOLIOS}/p[0-9]+$`);
			tests.push(orgRouteWithActivePortfolio);
		}

		// As per #12052, we should be able to sign up a new user while another one is logged in...
		tests.push(new RegExp(`^auth/new_user\\?tokenid=`));

		if (!user) {
			// 6) Routes that can be accessed only while logged out
			//
			tests.push(
				new RegExp(`^auth/login($|/)`),
				new RegExp(`^auth/login/sso($|/)`),
				new RegExp(`^auth/signin($|/)`),
				new RegExp(`^auth/forgotten_password`),
				new RegExp(`^auth/sso-login-failed`),
				new RegExp(`^auth/ssocallback`),
			);
		}

		const h = this.cleanHash(hash);

		return tests.some((t) => t.test(h));
	}

	public static decomposeParts(pattern: string): {value: string; isParam: boolean; optional: boolean}[] {
		const parts = pattern.split("/");
		const result = [];

		for (const part of parts) {
			const {result: paramValue, removed: isParam} = StringUtils.removeCharacter(part, ":");
			const {result: value, removed: optional} = StringUtils.removeCharacter(paramValue, "?");

			result.push({
				value: value,
				isParam: isParam,
				optional: optional,
			});
		}

		return result;
	}

	private static removeCharacter(value: string, char: string) {
		const result = value.split(char).join("");

		return {
			result: result,
			removed: result.length !== value.length,
		};
	}

	public static trim(value: string) {
		value = value || "";
		if (value.trim) {
			return value.trim();
		}
		return value;
	}

	public static leftPad(value: number | string, width: number, char = "0"): string {
		const n: string = `${value}`;

		return n.length >= width ? n : new Array(width - n.length + 1).join(char) + n;
	}

	// https://stackoverflow.com/questions/3410464/how-to-find-indices-of-all-occurrences-of-one-string-in-another-in-javascript
	public static getIndicesOfSubstring(source: string, find: string) {
		if (!source) {
			return [];
		}
		// if find is empty string return all indexes.
		if (!find) {
			// or shorter arrow function:
			return source.split("").map((_, i) => i);
		}
		const result = [];

		for (let i = 0; i < source.length; ++i) {
			// If you want to search case insensitive use
			if (source.substring(i, i + find.length).toLowerCase() === find) {
				result.push(i);
			}
		}

		return result;
	}

	public static equalsIgnoreCase(a: string, b: string) {
		a = a || "";
		b = b || "";
		if (!a.toLowerCase) {
			return false;
		}
		if (!b.toLowerCase) {
			return false;
		}

		a = a.toLowerCase();
		b = b.toLowerCase();

		return a === b;
	}

	public static containsIgnoreCase(source: string, search: string) {
		source = source || "";
		search = search || "";
		if (!source.toLowerCase) {
			return false;
		}
		if (!search.toLowerCase) {
			return false;
		}

		source = source.toLowerCase();
		search = search.toLowerCase();

		return source.includes(search);
	}

	public static sortIgnoreCase(a: string, b: string): 0 | 1 | -1 {
		a = a || "";
		b = b || "";
		if (!a.toLowerCase) {
			return 0;
		}
		if (!b.toLowerCase) {
			return 0;
		}

		a = a.toLowerCase();
		b = b.toLowerCase();

		return a < b ? -1 : 1;
	}

	public static regexHighlight(string: string, queryStr: string): string {
		if (!string) {
			return string;
		} else if (queryStr) {
			// https://stackoverflow.com/questions/7313395/case-insensitive-replace-all
			const esc = queryStr.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
			const reg = new RegExp(esc, "ig");
			return string.replace(reg, (str: string) => (str ? `<b>${str}</b>` : ""));
		} else {
			return string;
		}
	}

	public static emailValidator(email: string): boolean {
		if (typeof email !== "string") {
			return false;
		}
		// http://emailregex.com/
		return !!email.match(
			/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
		);
	}

	public static isBlank(value: any): boolean {
		if (value === null) {
			return true;
		}
		if (value === undefined) {
			return true;
		}
		if (value === false) {
			return false;
		} // false is not a blank value!

		if (StringUtils.trim(value) === "") {
			return true;
		}

		return false;
	}

	public static filterByKeywords(list: {keywords: string[]}[], searchValue: string) {
		return list.filter((element: {keywords: string[]}) => {
			for (const keyword of element.keywords) {
				if (StringUtils.containsIgnoreCase(keyword, searchValue)) {
					return true;
				}
			}

			return false;
		});
	}

	public static capitalize(value: string = "") {
		return value.charAt(0).toUpperCase() + value.slice(1);
	}

	public static decapitalize(value: string = "") {
		return value.charAt(0).toLowerCase() + value.slice(1);
	}

	public static lowerCase(value: string) {
		return value?.toLowerCase ? value.toLowerCase() : value;
	}

	public static titleCase(value: string = "") {
		return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
	}

	public static isTruthy(value: any): boolean {
		return value || value === 0 || value === false;
	}

	public static formatNumberWithCommas(value: number): string {
		const valueString = value.toString();

		if (valueString.includes("e")) {
			return valueString;
		}

		const decimalIndex = valueString.indexOf(".");
		const integerPart = decimalIndex === -1 ? valueString : valueString.slice(0, decimalIndex);
		const decimalPart = decimalIndex === -1 ? "" : valueString.slice(decimalIndex);
		const sign = integerPart.startsWith("-") ? "-" : "";
		let remainingDigits = sign ? integerPart.slice(1) : integerPart;
		const digitGroups: string[] = [];

		while (remainingDigits.length > 3) {
			digitGroups.unshift(remainingDigits.slice(-3));
			remainingDigits = remainingDigits.slice(0, -3);
		}
		digitGroups.unshift(remainingDigits);

		return `${sign}${digitGroups.join(",")}${decimalPart}`;
	}

	private static countSubstringInString(string: string, substring: string) {
		let count = 0;
		let index = string.indexOf(substring);
		while (index !== -1) {
			count++;
			index = string.indexOf(substring, index + substring.length);
		}

		return count;
	}

	// Parse a table string into a block of text
	// Reads the "tableString" until the first "double new line" character (if the separatorCount is at least as many as in the header),
	// then it assumes it's the end of the block
	// The idea is that we count the separators within the headers, and if the new line character (\n) comes sooner than the last separator on the specific line,
	// we assume it's a multiline field
	// We replace new line characters with <br/> in multiline fields, so it doesn't mess up the table structure!
	// We replace them back into new line characters within the "ClipboardManager"
	private static parseTableStringToBlock(blockString: string): {indexOfBlockEnd: number; content: string} {
		const separator = "\t";

		const firstNewLineIndex: number = blockString.indexOf("\n");

		if (firstNewLineIndex > -1 && blockString.length > 1) {
			const firstLine: string = blockString.substring(0, firstNewLineIndex);
			const separatorCountInHeader: number = StringUtils.countSubstringInString(firstLine, separator);
			let content: string = `${firstLine}\n`;
			let separatorCountInRow: number = 0;

			for (let i = firstNewLineIndex + 1; i < blockString.length; ++i) {
				let currentChar = blockString[i];
				if (currentChar === "\t") {
					separatorCountInRow++;
				}

				if (currentChar === "\n") {
					if (separatorCountInRow < separatorCountInHeader) {
						currentChar = "<br/>";
					} else if (i < blockString.length - 1 && blockString[i + 1] === "\n") {
						// if there are two new lines in a row (and there are at least as many tabs so far in the row as in the header), we assume it's the end of the block
						// Edge case: when the last cell of the row has new line character(s), it can mess up the parser, because we can't make a difference between that new line character,
						// and the "real" one that comes after the row...

						return {
							content: content.trim(),
							indexOfBlockEnd: i,
						};
					} else {
						// if the new line character is not followed by another new line character, we assume it's a new row
						separatorCountInRow = 0; // reset the separator count for the next row
					}
				}

				content += currentChar;
			}

			return {
				content: content.trim(),
				indexOfBlockEnd: blockString.length - 1,
			};
		}

		return {
			content: "",
			indexOfBlockEnd: -1,
		};
	}

	// Used when an excel/sheet table of xyicons/boundaries/markups are getting pasted
	public static formatTableString(tableString: string) {
		tableString = tableString
			.replace(/\r\n/g, "\n") // replaces \r\n newlines (windows-style) with unix style new lines (\n)
			.split("\n")
			.map((line) => this.removeTrailingCharacter(line, "\t"))
			.join("\n")
			.trimEnd(); // removes empty tabs, newlines, white-space characters from end of text

		const blocks: string[] = [];

		// We assume there's no "new line" character in the header names!
		let blockString = tableString.trimStart();
		let {content, indexOfBlockEnd} = this.parseTableStringToBlock(blockString);

		while (content.trim().length > 0) {
			blocks.push(content);
			blockString = blockString.substring(indexOfBlockEnd + 1);
			const blockObject = this.parseTableStringToBlock(blockString);

			content = blockObject.content;
			indexOfBlockEnd = blockObject.indexOfBlockEnd;
		}

		return blocks.join("\n\n");
	}

	private static removeTrailingCharacter(value: string, character: string): string {
		let endIndex = value.length;

		while (endIndex > 0 && value[endIndex - 1] === character) {
			endIndex--;
		}

		return value.slice(0, endIndex);
	}

	public static removeWhiteSpaces(text: string) {
		return text.trim().replace(/\s+/g, " ");
	}

	/**
	 * From:
	 * https://stackoverflow.com/questions/36288375/how-to-parse-csv-data-that-contains-newlines-in-field-using-javascript
	 *
	 * CSVToArray parses any String of Data including '\r' '\n' characters,
	 * and returns an array with the rows of data.
	 * @param {String} CSV_string - the CSV string you need to parse
	 * @param {String} delimiter - the delimeter used to separate fields of data
	 * @returns {Array} rows - rows of CSV where first row are column headers
	 */
	public static CSVToArray(CSV_string: string, delimiter: string = ",", maxColumns = Infinity): (string | number)[][] {
		try {
			delimiter = delimiter || ","; // user-supplied delimeter or default comma

			const pattern = new RegExp(
				// regular expression to parse the CSV values.
				// Delimiters:
				`(\\${delimiter}|\\r?\\n|\\r|^)` +
					// Quoted fields: "field",
					// it can possible be prefixed with an equal sign: \=?
					'(?:=?"([^"]*(?:""[^"]*)*)"|' +
					// Standard fields.
					`([^"\\${delimiter}\\r\\n]*))`,
				"gi",
			);

			const rows: (string | number)[][] = [[]]; // array to hold our data. First row is column headers.
			// array to hold our individual pattern matching groups:
			let matches: string[] | false = false; // false if we don't find any matches

			// Loop until we no longer find a regular expression match
			while ((matches = pattern.exec(CSV_string))) {
				const matched_delimiter = matches[1]; // Get the matched delimiter

				// Check if the delimiter has a length (and is not the start of string)
				// and if it matches field delimiter. If not, it is a row delimiter.
				if (matched_delimiter.length && matched_delimiter !== delimiter) {
					// Since this is a new row of data, add an empty row to the array.
					rows.push([]);
				}
				let matched_value;

				// Once we have eliminated the delimiter, check to see
				// what kind of value was captured (quoted or unquoted):
				if (matches[2] === "" || (matches[2] && matches[2].trim() === "")) {
					//Check whther its empty
					matched_value = "";
				} else if (matches[2]) {
					// found quoted value. unescape any double quotes.
					matched_value = matches[2].replace(new RegExp('""', "g"), '"');
				} else {
					//to catch if there an empty comming here and haddle it without returning 0
					if (matches[3] === "" || (matches[3] && matches[3].trim() === "")) {
						matched_value = "";
					} else if (matches[3]) {
						//found a quoted value for matches[3]
						matched_value = matches[3].replace(new RegExp('""', "g"), '"');
					}
				}
				// Now that we have our value string, let's add
				// it to the data array.

				const row = rows[rows.length - 1];

				if (row.length < maxColumns) {
					if (matched_value === "") {
						// Leave empty values as they are, don't convert them to numbers, because they'll become "0", and
						// according to #8586, we don't want that
					} else {
						const matchedValueAsNumber: number = Number(matched_value);

						if (!isNaN(matchedValueAsNumber)) {
							matched_value = matchedValueAsNumber;
						}
					}

					row.push(matched_value);
				}
			}
			return rows; // Return the parsed data Array
		} catch (e) {
			console.warn(e);
			return [];
		}
	}
}
